import React, { Component } from 'react';
import { Table, Label, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ui from '../../common/services/ui';
import { normalize, schema } from 'normalizr';
import moment from 'moment';
import LabelWithTooltip from '../../common/components/LabelWithTooltip';
import { Checkbox } from 'react-icheck';
import 'icheck/skins/minimal/grey.css';
import { ConfirmationButton } from '../../common/components/formControls';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faCamera, faDollar, faMedkit, faTimesCircle, faRightLeft } from '@fortawesome/free-solid-svg-icons';
import { faHandshake, faMoneyBill1 } from '@fortawesome/free-regular-svg-icons';
import NewEnrolmentButton from './NewEnrolmentButton';
import EnrolmentCount from './EnrolmentCount';

class LessonAttendanceTable extends Component {

	constructor(props) {
		super(props);

		this.handleChangeSelection = this.handleChangeSelection.bind(this);
	}

	childrenByLessonType(lessons, lessonType) {
		const filteredLessons = lessons.filter(lesson =>
			Array.isArray(lessonType) ? lessonType.includes(lesson.lessonType) : lesson.lessonType === lessonType
		);
		return this.getChildren(filteredLessons);
	}

	getChildren(childLessons) {
		const normalizedData = this.normalizeData(childLessons);
		return normalizedData && normalizedData.entities && normalizedData.entities.children;
	}

	normalizeData(childLessons) {
		const child = new schema.Entity('children', {}, {
			idAttribute: c => c.id
		});
		const lesson = new schema.Entity('lessons', { child: child }, {
			idAttribute: lesson => lesson.id
		});
		const normalizedData = normalize(childLessons, [lesson]);
		return normalizedData;
	}

	lessonCellLabel(lesson, t) {
		let text = null;
		switch (lesson.lessonType) {
			case 2:	// trial class
				text = t(lesson.isLastTrial && lesson.isPackageTrial ?	// last trial in a package
					'LessonAttendance_TypeTrialLast' : 'LessonAttendance_TypeTrial');
				break;
			case 3:	// make-up class
				text = t('LessonAttendance_TypeMakeup');
				break;
		}

		return <React.Fragment>
			{lesson.isFirstLessonForParent && <Label bsStyle='warning'>{t('Common_New').toLowerCase()}</Label>}
			{text && <Label bsStyle='warning' style={{ marginLeft: '3px' }}>{text}</Label>}
		</React.Fragment>;
	}

	lessonCellText(lesson, t) {
		let text = 'undefined';
		switch (lesson.lessonType) {
			case 1:
				text = t('LessonAttendance_TypeNormal');
				break;
			case 2:
				text = t('LessonAttendance_TypeTrial');
				break;
			case 3:
				text = t('LessonAttendance_TypeMakeup');
				break;
		}

		return text;
	}

	sameDate(first, second) {
		const firstParsed = moment(first).startOf('day');
		const secondParsed = moment(second).startOf('day');
		return firstParsed.isSame(secondParsed);
	}

	// Some children may have booked a normal lesson and paid lesson at the same time.
	// Exclude paid children from entity with trial children 
	removeNormal(trial, normal) {
		if (!trial || !normal) {
			return trial;
		}

		const filter = {};
		for (const childId in trial) {
			if (!normal[childId]) {
				filter[childId] = trial[childId];
			}
		}
		return filter;
	}

	renderChildCount(lessonDates, childLessons, selectedChildIds) {
		const { weeklyProgramItem, canSendMessage, canViewNotes, enableTrials, enableMakeUps, t } = this.props;

		if (!lessonDates || !weeklyProgramItem || !childLessons) {
			return null;
		}

		const allChildren = this.getChildren(childLessons);
		const allSelected = allChildren && Object.keys(allChildren).every(id => selectedChildIds.has(Number(id)));

		return (
			<tr>
				{canSendMessage && <th key={-3}>
					<Checkbox checkboxClass='icheckbox_minimal-grey bg' increaseArea='20%'
						checked={allSelected}
						onChange={(e, checked) => this.handleChangeSelection(allChildren && Object.values(allChildren), checked)}
					/>
				</th>}
				<th key={-2} />
				{canViewNotes && <th key={-1} />}
				{lessonDates.map(date => <th key={date} className={this.isCurrentWeek(date) ? 'current_week center' : 'center'} align='center' valign='middle'>
					<EnrolmentCount enableTrials={enableTrials} enableMakeUps={enableMakeUps} date={date} childLessons={childLessons} weeklyProgramItem={weeklyProgramItem} />
				</th>)}
			</tr>
		);
	}

	handleChangeSelection(children, checked) {
		const { selectedChildren, onMultipleSelectionChange } = this.props;

		if (children && onMultipleSelectionChange) {
			const currentSelection = selectedChildren ? [...selectedChildren] : [];

			children.forEach(child => {
				const existingRowIndex = currentSelection.findIndex(x => x.id === child.id);

				if (checked && existingRowIndex < 0) {
					currentSelection.push(child);
				}

				if (!checked && existingRowIndex >= 0) {
					currentSelection.splice(existingRowIndex, 1);
				}
			});

			onMultipleSelectionChange(currentSelection);
		}
	}

	renderChildrensLessons(children, lessons, lessonDates, selectedChildIds, t, isReadonly) {
		const { canSendMessage, canViewNotes } = this.props;

		if (!children || !lessonDates || !lessons) {
			return null;
		}
		let sortedChildren = Object.values(children).sort((a, b) => a.name.toUpperCase() < b.name.toUpperCase() ? -1 : 1);
		return Object.keys(sortedChildren).map(key => {
			const child = sortedChildren[key];
			const checkboxCell = canSendMessage && this.renderCheckboxCell(child, selectedChildIds);
			const childCell = this.renderChildCell(child, lessons, t);
			const notesCell = canViewNotes && this.renderNotesCell(child);
			const lessonCells = lessonDates.map((lessonDate, i) => {
				const previousDate = i > 0 ? lessonDates[i - 1] : null;
				return this.renderLessonCell(lessonDate, lessons, child, t, isReadonly, previousDate);
			});
			return (
				<tr key={key}>
					{[checkboxCell, childCell, notesCell, lessonCells]}
				</tr>
			);
		});
	}

	renderCheckboxCell(child, selectedChildIds) {
		return <td key={`select${child.id}`} style={{ width: 20 }}>
			<Checkbox checkboxClass='icheckbox_minimal-grey bg' increaseArea='20%'
				checked={selectedChildIds.has(child.id)} onChange={(e, checked) => this.handleChangeSelection([child], checked)}
			/>
		</td>;
	}

	renderNotesCell(child) {
		const { canViewNotes, onNotesClick, t } = this.props;

		return <td key={`notes${child.id}`}>
			{canViewNotes && <div>
				<div style={{ whiteSpace: 'nowrap' }}>
					<a onClick={() => onNotesClick(child)} style={{ cursor: 'pointer' }}>{t('Child_Section_Notes')}</a>
					{child.noteStats && child.noteStats.count > 0 && <Badge className='badge-green' style={{ marginLeft: 3 }}>{child.noteStats.count}</Badge>}
				</div>
				<span className='text-muted' style={{ fontSize: '12px' }}>{child.noteStats.lastCreated && moment(child.noteStats.lastCreated).fromNow()}</span>
			</div>}
		</td>
	}

	// opens the Move child lessons wizard on step 2, with this child and the class they are
	// currently in already selected
	renderMoveLessonsIcon(child, lessons, t) {
		// the current class is identified by the payment behind the child's lesson in this programme
		const { centerId, termId } = this.props;
		const childLessons = lessons.filter(l => l.child.id === child.id);
		const lesson = childLessons.find(l => l.lessonType === 1) || childLessons[0];
		const sourcePaymentId = lesson && lesson.paymentHistory && lesson.paymentHistory.id;

		const params = [`childId=${child.id}`, `parentId=${child.parent.id}`];
		if (sourcePaymentId) {
			params.push(`sourcePaymentId=${sourcePaymentId}`);
		}
		if (centerId && termId) {
			params.push(`centerId=${centerId}`, `termId=${termId}`);
		}

		// no colour of its own - the icon inherits the link colour, unlike the status icons next to it
		return <Link to={`/moveChildLessons?${params.join('&')}`}>
			<LabelWithTooltip id='moveLessons' tooltip={t('LessonAttendance_MoveToAnotherClass')}>
				<FontAwesomeIcon style={{ marginLeft: '5px' }} icon={faRightLeft} />
			</LabelWithTooltip>
		</Link>;
	}

	renderChildCell(child, lessons, t) {
		const parentText = `${child.parent.name} >>`;
		const { onInfoClick, enableCollectMedicalInformation, canMoveLessons } = this.props;
		const hasPendingApprovalChildLessons = lessons.some(l => l.child.id === child.id && l.paymentHistory.status === 6);
		const hasOutstandingInstalment = lessons.some(l => l.child.id === child.id && l.hasOutstandingInstalment);

		const medicalIconColor = child.hasAnyMedicalInformation ? 'red' : (child.completeMedicalInformationForm ? 'green' : 'orange');
		const medicalIconTooltip = child.hasAnyMedicalInformation ? 'Child_HasMedicalInformationWithIssuesTooltip' : (child.completeMedicalInformationForm ? 'Child_HasMedicalInformationTooltip' : 'Child_NoMedicalInformationTooltip');

		const medicalInfoIcon = enableCollectMedicalInformation && <LabelWithTooltip id='hasMedicalInformation' tooltip={t(medicalIconTooltip)}>
			<FontAwesomeIcon style={{ color: medicalIconColor, marginLeft: '5px' }} icon={faMedkit} />
		</LabelWithTooltip>;

		const photoConsentIcon = <LabelWithTooltip id='photoConsent' tooltip={t(child.parent.photoConsent ? 'Child_HasPhotoConsentTooltip' : 'Child_NoPhotoConsentTooltip')}>
			<FontAwesomeIcon style={{ color: child.parent.photoConsent ? 'green' : 'red', marginLeft: '5px' }} icon={faCamera} />
		</LabelWithTooltip>;

		const pendingApprovalIcon = hasPendingApprovalChildLessons && <LabelWithTooltip id='pendingApproval' tooltip={t('Child_HasPendingApprovalTooltip')}>
			<FontAwesomeIcon style={{ color: 'red', marginLeft: '5px' }} icon={faMoneyBill1} />
		</LabelWithTooltip>;

		const outstandingInstalmentIcon = hasOutstandingInstalment && <LabelWithTooltip id='outstandingInstalment' tooltip={t('Child_HasOutstandingInstalmentTooltip')}>
			<FontAwesomeIcon style={{ color: '#f0ad4e', marginLeft: '5px' }} icon={faDollar} />
		</LabelWithTooltip>;

		//allowed only if !AllowOverlappingTerms
		const enrolledInNextTermIcon = child.isEnrolledInNextTerm && <LabelWithTooltip id='isEnrolledInNextTerm' tooltip={t('Child_EnrolledInNextTermTooltip')}>
			<FontAwesomeIcon style={{ color: 'green', marginLeft: '5px' }} icon={faHandshake} />
		</LabelWithTooltip>;

		return (
			<td key={child.id}>
				<div>
					<Link to={`/parents/${child.parent.id}/children/${child.id}`} style={{ fontWeight: 'bold' }}>{child.name}</Link>
					{enableCollectMedicalInformation && <Link to={`/parents/${child.parent.id}/children/${child.id}/medical`}>{medicalInfoIcon}</Link>}
					{photoConsentIcon}
					{pendingApprovalIcon}
					{enrolledInNextTermIcon}
					{outstandingInstalmentIcon}
					{canMoveLessons && this.renderMoveLessonsIcon(child, lessons, t)}
					<div>
						<span style={{ whiteSpace: 'nowrap' }}>{ui.formatAge(child.dateOfBirth)}</span>
						<span className='text-muted' style={{ fontSize: '12px', marginLeft: 5 }}>{ui.formatShortDate(child.dateOfBirth)}</span>
					</div>
				</div>
				<div>
					<a href='#' onClick={() => onInfoClick(child.parent)} style={{ float: 'right', fontSize: '12px' }}>{parentText}</a>
				</div>
			</td>
		);
	}

	isCurrentWeek(date) {
		return moment(date) > moment().startOf('isoWeek') && moment(date) < moment().endOf('isoWeek');
	}

	renderHeader(lessonDates) {
		if (!lessonDates) {
			return null;
		}

		const { canSendMessage, canViewNotes } = this.props;

		return (
			<tr>
				{canSendMessage && <th key={-3} />}
				<th key={-2} />
				{canViewNotes && <th key={-1} />}
				{lessonDates.map(date => <th key={date} className={this.isCurrentWeek(date) ? 'current_week center' : 'center'} align='center' valign='middle'>
					{ui.customDateFormat(date, 'LL')}
				</th>)}
			</tr>
		);
	}

	renderButtons(lessonDates, t, isReadonly) {
		if (!lessonDates) {
			return null;
		}
		const { onNewClick, onNewMakeupClick, canSendMessage, canViewNotes, enableMakeUps } = this.props;
		return (
			<tr>
				{canSendMessage && <th key={-3} />}
				<th key={-2} />
				{canViewNotes && <th key={-1} />}
				{lessonDates.map((date, index) => <th key={date} className={this.isCurrentWeek(date) ? 'current_week center' : 'center'} align='center' valign='middle'>
					<NewEnrolmentButton key={date} id={`NewEnrolmentButton_${index}`} date={date} enableMakeUps={enableMakeUps} onNewClick={onNewClick} onNewMakeupClick={onNewMakeupClick} />
				</th>)}
			</tr>
		);
	}

	findLesson(date, childLessons, child) {
		const find = (lessonType) => childLessons.find(l => l.child.id === child.id && this.sameDate(l.lessonDate, date) && l.lessonType === lessonType);
		// Fetch child's paid lesson
		let lesson = find(1);
		if (lesson) {
			return lesson;
		}
		// If we didn't find a paid lesson for the child, we check if it has a trial lesson to display
		lesson = find(2);
		if (lesson) {
			return lesson;
		}
		// If we didn't find a paid lesson and trial lesson for the child, we check if it has a makeup lesson to display
		lesson = find(3);
		if (lesson) {
			return lesson;
		}
		return null;
	}

	renderLessonCell(lessonDate, childLessons, child, t, isReadonly, previousLessonDate) {
		const lesson = this.findLesson(lessonDate, childLessons, child);
		if (!lesson) {
			// If the child has neither paid or trial lesson, then we display an empty cell
			return <td key={lessonDate} className={this.isCurrentWeek(lessonDate) ? 'current_week center' : 'center'} align='center' valign='middle'>
				{
					// show option to Cancel trial reservation if this is the date after a trial with a reservation
					this.renderTrialOptions(previousLessonDate, childLessons, child, t)
				}
			</td>;
		}

		const isCredit = lesson.paymentHistory && lesson.paymentHistory.status === 5;
		const icon = this.renderLessonCellIcon(lesson, isCredit, isReadonly);
		const label = this.lessonCellLabel(lesson, t);
		return (
			<td key={lessonDate} className={this.isCurrentWeek(lessonDate) ? 'current_week center' : 'center'} align='center' valign='middle' style={{ position: 'relative' }}>
				<LabelWithTooltip id='childTooltip' tooltip={child && child.name} placement='left'>{icon}</LabelWithTooltip>
				<span style={{ position: 'absolute', top: 5, right: 5 }}>{label}</span>
			</td>
		);
	}

	renderTrialOptions(previousLessonDate, childLessons, child, t) {
		if (previousLessonDate) {
			const previousLesson = this.findLesson(previousLessonDate, childLessons, child);
			if (previousLesson && previousLesson.isLastTrial && previousLesson.lockClassesAfterTrial) {
				return <ConfirmationButton showLink={true} iconStyle={{ display: 'none' }}
					onConfirmation={() => this.props.onCancelTrialReservation && this.props.onCancelTrialReservation(previousLesson)}
					confirmationTitle={t('LessonAttendance_CancelTrialReservationTitle')}
					confirmationMessage={t('LessonAttendance_CancelTrialReservationMessage')}
					label={t('LessonAttendance_CancelTrialReservation')}
				/>;
			}
		}
		return null;
	}

	renderLessonCellIcon(lesson, isCredit, isReadonly) {
		const classes = lesson.isDebit ? 'save_attend debit_attend' : 'save_attend';
		const iconClasses = `attendance-icon ${isCredit ? 'yellow' : lesson.isAttended ? 'green' : 'red'}`;
		const icon = lesson.isAttended ? faCheckCircle : faTimesCircle;

		const { onLessonClick } = this.props;
		const linkProps = isReadonly ? {} : { href: 'javascript:void(0);', onClick: () => onLessonClick(lesson) };
		return (
			<a className={classes} {...linkProps}>
				<FontAwesomeIcon icon={icon} className={iconClasses} />
			</a>
		);
	}

	render() {
		const { isReadonly, showHeader, lessonDates, childLessons, t, canAddNewLessons, selectedChildren } = this.props;

		const selectedChildIds = new Set(selectedChildren && [...selectedChildren.map(x => x.id)]);

		const normalLessonsChildren = this.childrenByLessonType(childLessons, 1);
		const trialLessonsChildren = this.removeNormal(this.childrenByLessonType(childLessons, [2, 3]), normalLessonsChildren);
		const header = this.renderHeader(lessonDates);

		return (
			<Table bordered striped hover responsive>
				{showHeader && <thead>
					{header}
					{canAddNewLessons && this.renderButtons(lessonDates, t, isReadonly)}
					{this.renderChildCount(lessonDates, childLessons, selectedChildIds)}
				</thead>}
				<tbody>
					{this.renderChildrensLessons(normalLessonsChildren, childLessons, lessonDates, selectedChildIds, t, isReadonly)}
					{this.renderChildrensLessons(trialLessonsChildren, childLessons, lessonDates, selectedChildIds, t, isReadonly)}
				</tbody>
			</Table>
		);
	}
}

export default LessonAttendanceTable;